import { apiFetch, withApiRequestScope } from "../lib/api";
import type { WorkspaceListItem } from "./api";
import { hierarchyDestination } from "../hierarchy/urls";

// Account switching must discard mounted forms and account-bound query state.
// A document navigation also drops requests and closures from the old session.
export function reloadWorkspaceAccount(destination: string = "/workspaces"): void {
  window.location.replace(hierarchyDestination(destination)?.path ?? "/workspaces");
}

export interface WorkspaceSession {
  email: string;
  workspaces: WorkspaceListItem[];
}

export class WorkspaceSessionError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "WorkspaceSessionError";
  }
}

export async function fetchWorkspaceSession(signal?: AbortSignal): Promise<WorkspaceSession> {
  return withApiRequestScope(async (signal) => {
    const meResponse = await apiFetch("/auth/me", {
      credentials: "include",
      cache: "no-store",
      signal,
    });
    if (!meResponse.ok) {
      throw new WorkspaceSessionError(
        meResponse.status,
        "Couldn't confirm your session. Try signing in again.",
      );
    }
    const me = (await meResponse.json()) as { userId: string };
    signal.throwIfAborted();

    const workspacesResponse = await apiFetch("/workspaces", {
      credentials: "include",
      cache: "no-store",
      signal,
    });
    if (!workspacesResponse.ok) {
      throw new WorkspaceSessionError(
        workspacesResponse.status,
        "Couldn't load your workspaces. Please try again.",
      );
    }
    const { workspaces } = (await workspacesResponse.json()) as {
      workspaces: WorkspaceListItem[];
    };
    return { email: me.userId, workspaces };
  }, signal);
}

export async function requestWorkspaceLogout(): Promise<string | undefined> {
  const response = await apiFetch("/auth/logout", {
    method: "POST",
    credentials: "include",
  }, { retries: 0 });
  if (!response.ok) throw new WorkspaceSessionError(response.status, "Sign-out couldn't be confirmed. Please try again.");
  const body = (await response.json()) as { logoutUrl?: string };
  return typeof body.logoutUrl === "string" && body.logoutUrl.length > 0
    ? body.logoutUrl
    : undefined;
}
